import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { Loading } from '@/components/customComponents'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { chartColors } from '@/constants/COLOR_PALETTE'
import { TranslationFunctionType } from '@/i18n'
import { formatNumber } from '@/utils'

type ContentType = 'money' | 'percentage' | 'hour' | 'text' | 'number'

interface PnlLensTableColumn {
  header: string
  accessorKey: string
  type: ContentType
}

interface PnlLensTableData {
  [key: string]: string | number | undefined
}

const getOverviewOfKpisColumns = (
  t: TranslationFunctionType
): PnlLensTableColumn[] => [
  { header: '', accessorKey: 'values', type: 'text' },
  {
    header: t('clarityLens.pnlLens.tableHeaders.storesEom'),
    accessorKey: 'storesEom',
    type: 'number',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.openDays'),
    accessorKey: 'openDays',
    type: 'number',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.netRev'),
    accessorKey: 'netRev',
    type: 'number',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.nadr'),
    accessorKey: 'nadr',
    type: 'number',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.fourWallPercent'),
    accessorKey: 'fourWallPercent',
    type: 'percentage',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.fourWall'),
    accessorKey: 'fourWall',
    type: 'number',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.ebitda'),
    accessorKey: 'ebitda',
    type: 'number',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.ebitdaPercent'),
    accessorKey: 'ebitdaPercent',
    type: 'percentage',
  },
  {
    header: t('clarityLens.pnlLens.tableHeaders.eomCash'),
    accessorKey: 'eomCash',
    type: 'number',
  },
]

const getOverviewOfKpisDataStore1 = (
  t: TranslationFunctionType
): PnlLensTableData[] => [
  {
    values: t('clarityLens.pnlLens.values.actual'),
    storesEom: '45',
    openDays: '1380',
    netRev: '7.4',
    nadr: '5.4',
    fourWallPercent: '36%',
    fourWall: '2.7',
    ebitda: '1.9',
    ebitdaPercent: '15%',
    eomCash: '36.5',
  },
  {
    values: t('clarityLens.pnlLens.values.actualYtd'),
    storesEom: '45',
    openDays: '6,461',
    netRev: '28.3',
    nadr: '4.4',
    fourWallPercent: '30%',
    fourWall: '8.5',
    ebitda: '2.2',
    ebitdaPercent: '5%',
    eomCash: '36.5',
  },
  {
    values: t('clarityLens.pnlLens.values.budget'),
    storesEom: '47',
    openDays: '1457',
    netRev: '6.5',
    nadr: '4.3',
    fourWallPercent: '29%',
    fourWall: '1.9',
    ebitda: '0.6',
    ebitdaPercent: '6%',
    eomCash: '14.5',
  },
  {
    values: t('clarityLens.pnlLens.values.budgetYtd'),
    storesEom: '47',
    openDays: '6,515',
    netRev: '25.2',
    nadr: '3.9',
    fourWallPercent: '27%',
    fourWall: '6.9',
    ebitda: '(1.4)',
    ebitdaPercent: '(3%)',
    eomCash: '[14.5]',
  },
]

const getOverviewOfKpisDataStore2 = (
  t: TranslationFunctionType
): PnlLensTableData[] => [
  {
    values: t('clarityLens.pnlLens.values.actual'),
    storesEom: '45',
    openDays: '1380',
    netRev: '7.4',
    nadr: '5.4',
    fourWallPercent: '36%',
    fourWall: '2.7',
    ebitda: '1.9',
    ebitdaPercent: '15%',
    eomCash: '36.5',
  },
  {
    values: t('clarityLens.pnlLens.values.actualYtd'),
    storesEom: '45',
    openDays: '6,461',
    netRev: '28.3',
    nadr: '4.4',
    fourWallPercent: '30%',
    fourWall: '8.5',
    ebitda: '2.2',
    ebitdaPercent: '5%',
    eomCash: '36.5',
  },
  {
    values: t('clarityLens.pnlLens.values.budget'),
    storesEom: '47',
    openDays: '1457',
    netRev: '6.5',
    nadr: '4.3',
    fourWallPercent: '29%',
    fourWall: '1.9',
    ebitda: '0.6',
    ebitdaPercent: '6%',
    eomCash: '14.5',
  },
  {
    values: t('clarityLens.pnlLens.values.budgetYtd'),
    storesEom: '47',
    openDays: '6,515',
    netRev: '25.2',
    nadr: '3.9',
    fourWallPercent: '27%',
    fourWall: '6.9',
    ebitda: '(1.4)',
    ebitdaPercent: '(3%)',
    eomCash: '[14.5]',
  },
]

// Mock data for bar charts
const QUARTERLY_DATA_VISTA = [
  { month: 'July', year: 2024, value: 57 },
  { month: 'Aug', year: 2024, value: 69 },
  { month: 'Sep', year: 2024, value: 79 },
]

const QUARTERLY_DATA_HARRINGTON = [
  { month: 'July', year: 2024, value: 52 },
  { month: 'Aug', year: 2024, value: 64 },
  { month: 'Sep', year: 2024, value: 73 },
]

interface MonthlyData {
  month: string
  year: number
  value: number
}

interface MonthlyBarChartProps {
  title: string
  data: MonthlyData[]
  color: string
  height?: string
  showYear?: boolean
}

function MonthlyBarChart({
  title,
  data,
  color,
  showYear = false,
  t,
}: MonthlyBarChartProps & { t: TranslationFunctionType }): JSX.Element {
  // Calculate dynamic bar width based on number of months
  const getBarWidth = (dataLength: number): string => {
    if (dataLength <= 3) return '60%'
    if (dataLength <= 6) return '50%'
    if (dataLength <= 9) return '40%'
    return '30%'
  }

  // Calculate grid padding based on number of months
  const getGridSettings = (dataLength: number) => {
    const leftPadding = dataLength > 6 ? '5%' : '10%'
    const rightPadding = dataLength > 6 ? '5%' : '10%'
    return { left: leftPadding, right: rightPadding }
  }

  // Format x-axis labels with or without year
  const formatXAxisLabel = (item: MonthlyData): string => {
    if (showYear) {
      return `${item.month}\n${item.year}`
    }
    return item.month
  }

  // Check if data spans multiple years
  const hasMultipleYears = () => {
    const years = new Set(data.map((item) => item.year))
    return years.size > 1
  }

  // Auto-show year if multiple years are present
  const shouldShowYear = showYear || hasMultipleYears()

  const gridSettings = getGridSettings(data.length)

  const chartOptions: EChartsOption = {
    grid: {
      top: '20%',
      bottom: shouldShowYear ? '25%' : '20%', // More space for year labels
      left: gridSettings.left,
      right: gridSettings.right,
    },
    xAxis: {
      type: 'category',
      data: data.map((item) => formatXAxisLabel(item)),
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#626469',
        fontSize: 12,
        rotate: data.length > 6 ? 45 : 0, // Rotate labels if many months
        lineHeight: shouldShowYear ? 16 : 12, // Better spacing for multi-line labels
      },
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        type: 'bar',
        data: data.map((item) => item.value),
        itemStyle: {
          color: color,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: '#fff',
        },
        barWidth: getBarWidth(data.length),
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => `$${params.value}M`,
          color: '#14181f',
          fontSize: data.length > 9 ? 12 : 14, // Smaller font for many months
          fontWeight: 'bold',
        },
      },
    ],
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const dataIndex = params.dataIndex
        const item = data[dataIndex]
        return `${item.month} ${item.year}: $${params.value}M`
      },
    },
  }

  return (
    <div>
      <h3 className="leading-none tracking-tight text-black-500 text-display-s p-4 pt-0">
        {title}
      </h3>
      <div>
        <ReactEcharts option={chartOptions} />
      </div>
      <div className="grid grid-cols-2 gap-4 p-4">
        <Card className="flex flex-row gap-2 p-4">
          <h2 className="leading-none tracking-tight">
            {t('clarityLens.pnlLens.labels.nadr')}
          </h2>
          <h2 className="leading-none tracking-tight">$4.4M</h2>
        </Card>
        <Card className="flex flex-row gap-2 p-4">
          <h2 className="leading-none tracking-tight">
            {t('clarityLens.pnlLens.labels.adj')}
          </h2>
          <h2 className="leading-none tracking-tight">36%</h2>
        </Card>
      </div>
    </div>
  )
}

interface PnLLensComparisonBarChartProps {
  title: string
  dataSelected: MonthlyData[]
  dataComparison: MonthlyData[]
  selectedTitle: string
  comparisonTitle: string
}

function PnLLensComparisonBarChart({
  title,
  dataSelected,
  dataComparison,
  selectedTitle,
  comparisonTitle,
  t,
}: PnLLensComparisonBarChartProps & {
  t: TranslationFunctionType
}): JSX.Element {
  return (
    <Card>
      <h3 className="leading-none tracking-tight text-black-500 text-display-s p-4">
        {title}
      </h3>
      <div className="flex flex-row gap-4">
        <div className="w-[50%]">
          <MonthlyBarChart
            title={selectedTitle}
            data={dataSelected}
            color={chartColors[0]}
            t={t}
          />
        </div>
        <Separator orientation="vertical" className="h-[430px]" />
        <div className="w-[50%]">
          <MonthlyBarChart
            title={comparisonTitle}
            data={dataComparison}
            color={chartColors[1]}
            t={t}
          />
        </div>
      </div>
    </Card>
  )
}

interface PnlLensTableContentProps {
  columns: PnlLensTableColumn[]
  data: PnlLensTableData[] | undefined
}

const formatContent = ({
  content,
  type = 'text',
}: {
  content: string | number | undefined
  type?: ContentType
}): React.ReactNode => {
  switch (type) {
    case 'hour':
      return (
        <span>
          {formatNumber(content, type, false)}
          <span className="text-sm">hr</span>
        </span>
      )
    case 'number':
    case 'money':
    case 'percentage':
      return formatNumber(content, type)
    case 'text':
    default:
      return <span>{content}</span>
  }
}

const renderCell = (
  item: PnlLensTableData,
  column: PnlLensTableColumn,
  columnIndex: number
): JSX.Element => {
  const value = item[column.accessorKey]

  return (
    <TableCell
      key={column.accessorKey}
      className={`h-[78px] ${columnIndex === 0 ? 'sticky left-0 bg-gray-50 z-10 whitespace-nowrap' : ''}`}
    >
      <div className="flex">
        {formatContent({ content: value, type: column.type })}
      </div>
    </TableCell>
  )
}

function PnlLensTableContent({
  columns,
  data,
}: PnlLensTableContentProps): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column, index) => (
              <TableHead
                key={column.accessorKey}
                className={`whitespace-nowrap ${index === 0 ? 'sticky left-0 bg-gray-50 z-10' : ''}`}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map((item, index) => (
            // border none ?
            <TableRow key={index}>
              {columns.map((column, columnIndex) =>
                renderCell(item, column, columnIndex)
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

interface PnlLensTableProps {
  title: string
  columns: PnlLensTableColumn[]
  selectedTitle: string
  dataSelected: PnlLensTableData[] | undefined
  comparisonTitle: string
  dataComparison: PnlLensTableData[] | undefined
  loading?: boolean
}

function PnlLensTable({
  title,
  columns,
  selectedTitle,
  dataSelected,
  comparisonTitle,
  dataComparison,
  loading = true,
}: PnlLensTableProps): JSX.Element {
  return (
    <Card>
      <h3 className="leading-none tracking-tight text-black-500 text-display-s p-4">
        {title}
      </h3>
      {loading ? (
        <div className="flex justify-center items-center h-full p-4">
          <Loading />
        </div>
      ) : (
        <div className="flex flex-row">
          <div className="w-[50%]">
            <h3 className="leading-none tracking-tight text-black-500 text-display-s p-4 pt-0">
              {selectedTitle}
            </h3>
            <PnlLensTableContent columns={columns} data={dataSelected} />
          </div>
          <Separator orientation="vertical" className="h-[400px]" />
          <div className="w-[50%]">
            <h3 className="leading-none tracking-tight text-black-500 text-display-s p-4 pt-0">
              {comparisonTitle}
            </h3>
            <PnlLensTableContent columns={columns} data={dataComparison} />
          </div>
        </div>
      )}
    </Card>
  )
}

export default function PnlLensPage() {
  const { t } = useTranslation()

  return (
    <div className="space-y-6">
      {/* kpis table */}
      <PnlLensTable
        title={t('clarityLens.pnlLens.overviewOfKpis', {
          month: 'May',
          year: '2025',
        })}
        columns={getOverviewOfKpisColumns(t)}
        selectedTitle="Vista"
        comparisonTitle="Harrington"
        dataSelected={getOverviewOfKpisDataStore1(t)}
        dataComparison={getOverviewOfKpisDataStore2(t)}
        loading={false}
      />
      {/* bar chart */}
      <PnLLensComparisonBarChart
        title={t('clarityLens.pnlLens.kpiDeepDive')}
        selectedTitle="Vista"
        comparisonTitle="Harrington"
        dataSelected={QUARTERLY_DATA_VISTA}
        dataComparison={QUARTERLY_DATA_HARRINGTON}
        t={t}
      />
    </div>
  )
}
